import React from 'react';
import { rem } from '../lib/rem';

// THE app-wide status badge (Time & Expense rebuild contract, T2.4).
// One pill for every workflow status; add statuses here, never re-roll locally.

export type StatusVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const VARIANT_STYLES: Record<StatusVariant, { bg: string; color: string }> = {
  neutral: {
    bg: 'color-mix(in srgb, var(--tm) 12%, transparent)',
    color: 'var(--tm)',
  },
  info: {
    bg: 'color-mix(in srgb, var(--color-info) 13%, transparent)',
    color: 'var(--color-info)',
  },
  success: {
    bg: 'color-mix(in srgb, var(--color-success) 13%, transparent)',
    color: 'var(--color-success)',
  },
  warning: {
    bg: 'color-mix(in srgb, var(--color-warning) 16%, transparent)',
    color: 'var(--color-warning)',
  },
  danger: {
    bg: 'color-mix(in srgb, var(--color-danger) 13%, transparent)',
    color: 'var(--color-danger)',
  },
};

const KNOWN_STATUSES: Record<string, { label: string; variant: StatusVariant }> = {
  draft: { label: 'Draft', variant: 'neutral' },
  submitted: { label: 'Submitted', variant: 'info' },
  pending: { label: 'Pending', variant: 'info' },
  pending_review: { label: 'Pending review', variant: 'info' },
  approved: { label: 'Approved', variant: 'success' },
  confirmed: { label: 'Confirmed', variant: 'success' },
  locked: { label: 'Locked', variant: 'neutral' },
  rejected: { label: 'Rejected', variant: 'danger' },
  denied: { label: 'Denied', variant: 'danger' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
  active: { label: 'Active', variant: 'success' },
  archived: { label: 'Archived', variant: 'neutral' },
  sent: { label: 'Sent', variant: 'info' },
  paid: { label: 'Paid', variant: 'success' },
  void: { label: 'Void', variant: 'neutral' },
  overdue: { label: 'Overdue', variant: 'danger' },
  new: { label: 'New', variant: 'info' },
};

export interface StatusBadgeProps {
  status: string;
  /** Override the derived label (e.g. localized or richer copy). */
  label?: string;
  /** Override the derived variant for statuses not in the shared map. */
  variant?: StatusVariant;
  style?: React.CSSProperties;
}

export function StatusBadge({ status, label, variant, style }: StatusBadgeProps) {
  const known = KNOWN_STATUSES[status] ?? { label: status, variant: 'neutral' as StatusVariant };
  const v = VARIANT_STYLES[variant ?? known.variant];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: `${rem(3)} var(--space-3)`,
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--font-size-sm)',
        fontWeight: 700,
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        background: v.bg,
        color: v.color,
        ...style,
      }}
    >
      {label ?? known.label}
    </span>
  );
}

export default StatusBadge;
